/*
 * Category-card video playback.
 *
 * The Liquid renders `<video>` without an `autoplay` attribute on purpose, so
 * nothing plays until this script decides it should:
 *
 * - reduced motion  -> never plays; the poster frame is what the visitor sees
 * - off screen      -> never loads; `preload="none"` plus IntersectionObserver
 *                      means the bytes are only fetched when the card is near
 * - on screen       -> muted + inline playback starts, and pauses again when
 *                      the card scrolls away so background tabs stay cheap
 *
 * Safe to run more than once: every element carries a `data-skafe-video-ready`
 * guard, so extra copies of this script (one per section instance) no-op.
 */
(() => {
  'use strict';

  const wraps = document.querySelectorAll('[data-skafe-category-video]');
  if (!wraps.length) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Only one of the desktop/mobile pair is visible at a time; the hidden one
  // has no layout box, so we never waste bandwidth on it.
  const visibleVideos = (wrap) =>
    Array.from(wrap.querySelectorAll('video')).filter((v) => v.offsetParent !== null || v.getClientRects().length);

  const play = (video) => {
    if (reduceMotion.matches) return;
    // `muted` must be set as a property too — the attribute alone is not always
    // enough for autoplay policies on iOS.
    video.muted = true;
    video.playsInline = true;
    if (video.preload === 'none') video.preload = 'metadata';
    const attempt = video.play();
    // A rejected play() is fine: the poster stays up. Never let it surface as
    // an unhandled rejection.
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
  };

  const pause = (video) => {
    if (!video.paused) video.pause();
  };

  if (!('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const videos = visibleVideos(entry.target);
        videos.forEach(entry.isIntersecting ? play : pause);
      });
    },
    { rootMargin: '100px', threshold: 0.25 }
  );

  wraps.forEach((wrap) => {
    if (wrap.dataset.skafeVideoReady) return;
    wrap.dataset.skafeVideoReady = 'true';
    wrap.querySelectorAll('video').forEach((video) => {
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('tabindex', '-1');
      video.setAttribute('aria-hidden', 'true');
    });
    observer.observe(wrap);
  });

  // Honour a mid-session change to the motion preference.
  const onPreferenceChange = () => {
    wraps.forEach((wrap) => {
      const videos = Array.from(wrap.querySelectorAll('video'));
      if (reduceMotion.matches) {
        videos.forEach((video) => {
          pause(video);
          video.currentTime = 0;
        });
      } else {
        visibleVideos(wrap).forEach(play);
      }
    });
  };

  if (typeof reduceMotion.addEventListener === 'function') {
    reduceMotion.addEventListener('change', onPreferenceChange);
  }
})();
